import { createEntityAdapter, createReducer } from '@reduxjs/toolkit';

import { hasAsyncDispatch } from '../../utils/redux-store/asyncDispatchMiddleware';
import type { TaskInstance } from './taskSchemas';
import * as actions from './tasksActions';
import { mergeTaskDump } from './tasksBump';
import type { TasksState } from './tasksTypes';

export const tasksAdapter = createEntityAdapter<TaskInstance, string>({
  selectId: (task) => task.id,
});

export const initialTasksState: TasksState = tasksAdapter.getInitialState({
  error: null,
  fetchInvalidated: false,
  isLoading: false,
  loadPending: true,
  writeError: null,
});

const reducer = createReducer(initialTasksState, (builder) => {
  builder
    .addCase(actions.getTasks, (state) => {
      state.error = null;
      state.fetchInvalidated = false;
      state.isLoading = true;
    })
    .addCase(actions.getTasksSuccess, (state, action) => {
      if (state.fetchInvalidated && hasAsyncDispatch(action)) {
        state.fetchInvalidated = false;
        action.asyncDispatch(actions.getTasks(undefined, { context: 'TASKS_RECONCILE_AFTER_FETCH' }));
        return;
      }

      tasksAdapter.setAll(state, action.payload.resources);
      state.error = null;
      state.fetchInvalidated = false;
      state.isLoading = false;
      state.loadPending = false;
    })
    .addCase(actions.getTasksFailure, (state, action) => {
      if (state.fetchInvalidated && hasAsyncDispatch(action)) {
        state.fetchInvalidated = false;
        action.asyncDispatch(actions.getTasks(undefined, { context: 'TASKS_RECONCILE_AFTER_FETCH' }));
        return;
      }

      state.error = action.payload;
      state.fetchInvalidated = false;
      state.isLoading = false;
      state.loadPending = false;
    })
    .addCase(actions.clearTasks, (state) => {
      tasksAdapter.removeAll(state);
      state.error = null;
      state.fetchInvalidated = false;
      state.isLoading = false;
      state.loadPending = false;
      state.writeError = null;
    })
    .addCase(actions.reconcileTasks, (state, action) => {
      // A full fetch replaces the collection. Mark one that predates confirmed service data as stale
      // so its response cannot overwrite the reconciliation. Its completion schedules one follow-up
      // fetch, coalescing any other changes that arrive while it is in flight.
      const invalidatePendingFetch = state.isLoading;
      const current = tasksAdapter.getSelectors().selectAll(state);
      const merged = mergeTaskDump(current, action.payload.resources, {
        allowEqualVersion: action.payload.allowEqualVersion,
      });

      if (merged.outcome === 'refetch') {
        if (invalidatePendingFetch) {
          state.fetchInvalidated = true;
        } else if (hasAsyncDispatch(action)) {
          action.asyncDispatch(actions.getTasks(undefined, { context: 'TASKS_RECONCILE_REFETCH' }));
        }
        return;
      }

      if (!merged.changed) return;

      tasksAdapter.setAll(state, merged.tasks);
      if (invalidatePendingFetch) state.fetchInvalidated = true;
    })
    .addCase(actions.transitionTask, (state, { payload }) => {
      const task = state.entities[payload.id];
      if (!task || task.status === payload.status) return;
      task.status = payload.status;
    })
    .addCase(actions.rollbackTask, (state, { payload }) => {
      const task = state.entities[payload.id];
      if (task?.status === payload.optimisticStatus && task.dbVersion === payload.optimisticVersion) {
        task.status = payload.previousStatus;
      }
    })
    .addCase(actions.taskWriteSucceeded, (state) => {
      state.writeError = null;
    })
    .addCase(actions.taskWriteFailed, (state, { payload }) => {
      state.writeError = payload;
    });
});

export default reducer;
